import React, {memo, useState} from 'react';
import {Modal, Platform, StyleSheet, View, Dimensions} from 'react-native';
import Styles from '../Styles';
import Localized from 'src/constants/AppStrings';
import AVText from './AVText';
import NavActions from 'src/actions/NavActions';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';

export type SuccessDialogProps = {
  title: string;
  desc: string;
  isRedirectToHome?: boolean;
  testIDTitle?: string;
  testIDButton?: string;
  testIDMsg?: string;
};

const screenWidth = Dimensions.get('window').width;
const modalWidth = screenWidth * 0.8;

export const SuccessDialog = memo(
  ({
    title,
    isRedirectToHome,
    testIDTitle,
    testIDButton,
    testIDMsg,
  }: SuccessDialogProps) => {
    const [showSuccessModal, setShowSuccessModal] = useState(true);

    const onSuccessModalClose = () => {
      setShowSuccessModal(false);
      if (isRedirectToHome) {
        NavActions.popToTop();
      } else {
        NavActions.pop();
      }
    };

    if (!showSuccessModal) {
      return null;
    }

    return (
      <Modal
        visible={showSuccessModal}
        onDismiss={onSuccessModalClose}
        transparent
      >
        <View style={styles.successModalView}>
          <View style={styles.successModalContainer}>
            <AVTouchableOpacity
              aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} offer details`}
              role="button"
              style={styles.closeModalTitleContainer}
              onPress={onSuccessModalClose}
              accessible
              accessibilityLabel={Localized.Buttons.cancel}
              accessibilityRole="button"
              hitSlop={Styles.HitSlop_10}
            >
              <CancelIcon />
            </AVTouchableOpacity>
            <View style={styles.successModalViewContent}>
              <AVText
                aria-label={`${Localized.Success.success} label, text`}
                accessibilityLabel={`${Localized.Success.success} label`}
                style={getDescriber().getSuccessDialogDescriptor().header}
                accessible
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
                testID={testIDTitle}
                nativeID={testIDTitle}
              >
                {Localized.Success.success}
              </AVText>
              <AVText
                accessible
                accessibilityLabel={`${title} label`}
                aria-label={`${title} label, text`}
                style={[
                  getDescriber().getSuccessDialogDescriptor().title,
                  {textAlign: 'center'},
                ]}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm2}
                testID={testIDMsg}
                nativeID={testIDMsg}
              >
                {title}
              </AVText>
            </View>
            {getDescriber().getSuccessDialogBtn(
              onSuccessModalClose,
              testIDButton,
            )}
          </View>
        </View>
      </Modal>
    );
  },
);

const styles = StyleSheet.create({
  successModalViewContent: {
    marginTop: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successModalView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  successModalContainer: {
    minHeight: Platform.OS === 'ios' ? 220 : 230,
    width: modalWidth,
    backgroundColor: Styles.white,
    borderRadius: Styles.Spacing.m3,
    paddingHorizontal: 25,
  },
  closeModalTitleContainer: {
    justifyContent: 'flex-end',
    position: 'absolute',
    top: Styles.Heights.h1,
    right: Styles.Heights.h1,
  },
});
